import React, { useRef, useState, useMemo } from "react";
import { Box, Drawer, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";

interface EventSelectionWindowProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    registrations: any[];
    onSelect: (registration: any) => void;
    title?: string;
}

export const EventSelectionWindow = ({ open, setOpen, registrations, onSelect, title }: EventSelectionWindowProps) => {

    const [searchInput, setSearchInput] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSearchChange = (val: string) => {
        setSearchInput(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedQuery(val), 300);
    };

    const handleClose = () => {
        setSearchInput('');
        setDebouncedQuery('');
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setOpen(false);
    };

    const displayedRegistrations = useMemo(() => {
        const valid = registrations.filter(r =>
            r.eventDetails?.startDate &&
            (r.teeTimes?.length > 0 || r.eventDetails?.groupSelfSelection)
        );

        const sorted = [...valid].sort(
            (a, b) => new Date(b.eventDetails.startDate).getTime() - new Date(a.eventDetails.startDate).getTime()
        );

        const query = debouncedQuery.trim().toLowerCase();
        if (query) {
            return sorted.filter(r =>
                r.eventDetails.eventName?.toLowerCase().includes(query) ||
                r.eventDetails.courseName?.toLowerCase().includes(query)
            );
        }

        const today = new Date();
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

        const recentWindow = sorted.filter(r =>
            new Date(r.eventDetails.startDate) >= ninetyDaysAgo
        );

        const lastFive = sorted.slice(0, 5);

        return recentWindow.length >= lastFive.length ? recentWindow : lastFive;
    }, [registrations, debouncedQuery]);

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    height: '70dvh',
                    borderRadius: '16px 16px 0 0',
                    backgroundColor: 'sepia.background',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Drag handle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'sepia.light',
                    opacity: 0.7,
                }} />
            </Box>

            {/* Header */}
            <Box sx={{ px: 3, pt: 1, pb: 2, flexShrink: 0 }}>
                <Typography variant="h6" sx={{ color: 'sepia.dark', fontWeight: 700, mb: 2 }}>
                    {title ?? 'Select Your Event'}
                </Typography>
                <TextField
                    fullWidth
                    placeholder="Search events or courses..."
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'sepia.main', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 3,
                            '& fieldset': { borderColor: 'sepia.extraLight' },
                            '&:hover fieldset': { borderColor: 'sepia.light' },
                            '&.Mui-focused fieldset': { borderColor: 'sepia.main' },
                        },
                        '& .MuiInputBase-input': {
                            color: 'sepia.dark',
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: 'sepia.light',
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            {/* Event list */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
                {displayedRegistrations.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                        <Typography variant="body2" sx={{ color: 'sepia.light' }}>
                            No events found
                        </Typography>
                    </Box>
                ) : (
                    <Stack direction="column" spacing={1.5}>
                        {displayedRegistrations.map((registration: any) => {
                            const hasEventScore = Boolean(registration.eventScoreID);
                            const isGroupSelfSelection = Boolean(registration.eventDetails?.groupSelfSelection);
                            const isFuture = new Date(registration.eventDetails.startDate).getTime() > Date.now();
                            const isDisabled = isFuture || (!hasEventScore && !isGroupSelfSelection);
                            return (
                                <Box
                                    key={registration._id}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        onSelect(registration);
                                        setOpen(false);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: isDisabled ? 'rgba(255,255,255,0.4)' : 'sepia.background',
                                        borderRadius: 3,
                                        px: 2,
                                        py: 1.5,
                                        boxShadow: '0 1px 4px rgba(92, 51, 23, 0.10)',
                                        border: '1px solid',
                                        borderLeft: '4px solid',
                                        borderColor: isDisabled ? 'sepia.extraLight' : 'primary.light',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        opacity: isDisabled ? 0.55 : 1,
                                        pointerEvents: isDisabled ? 'none' : 'auto',
                                        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
                                        '&:hover': isDisabled ? {} : {
                                            boxShadow: '0 3px 10px rgba(92, 51, 23, 0.20)',
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    {/* Text content */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                color: 'sepia.dark',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {registration.eventDetails.eventName}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'sepia.main',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: 'block',
                                            }}
                                        >
                                            {registration.eventDetails.courseName}
                                        </Typography>
                                    </Box>

                                    {/* Date badge */}
                                    <Box sx={{
                                        ml: 1.5,
                                        flexShrink: 0,
                                        backgroundColor: 'primary.light',
                                        border: '1px solid',
                                        borderColor: 'primary.dark',
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.5,
                                        textAlign: 'center',
                                        minWidth: '60px',
                                    }}>
                                        <Typography variant="caption" sx={{ color: 'sepia.background', fontWeight: 600, fontSize: '0.65rem' }}>
                                            {formatDate(registration.eventDetails.startDate)}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Drawer>
    );
};
